import { useEffect, useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenueRealtime } from "./VenueRealtimeProvider";

/** Grace before a dropped stream is surfaced as "not live" — a blip shouldn't alarm the floor. */
const GRACE_MS = 2500;

/**
 * The connection indicator (features/realtime-sync.md degraded mode). `open` → a discreet green dot;
 * a drop that outlasts the grace → an amber "not live — refresh to see others' changes" warning (the
 * page still works, live push is just down). `role="status"` so assistive tech announces the change.
 */
export function LiveIndicator() {
  const status = useVenueRealtime();
  const { t } = useTranslation();
  const [degraded, setDegraded] = useState(false);

  // Clear the degraded flag the moment the connection reopens — adjusted during render (comparing
  // against the last-seen status) rather than via an effect, so the reset itself isn't a synchronous
  // setState-in-effect; the grace-period TIMER below still needs a real effect (an external system).
  const [lastStatus, setLastStatus] = useState(status);
  if (status !== lastStatus) {
    setLastStatus(status);
    if (status === "open") setDegraded(false);
  }

  useEffect(() => {
    if (status === "open") return;
    const id = setTimeout(() => setDegraded(true), GRACE_MS);
    return () => clearTimeout(id);
  }, [status]);

  if (status === "open") {
    return (
      <output
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "1.5",
          fontSize: "sm",
        })}
      >
        <span aria-hidden className={css({ w: "2", h: "2", rounded: "full", bg: "green.9" })} />
        {t("shell.live.live")}
      </output>
    );
  }

  if (degraded) {
    return (
      <output
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "1.5",
          fontSize: "sm",
          color: "amber.11",
          fontWeight: "medium",
        })}
      >
        <span aria-hidden className={css({ w: "2", h: "2", rounded: "full", bg: "amber.9" })} />
        {t("shell.live.notLive")}
      </output>
    );
  }

  return (
    <output className={css({ fontSize: "sm", color: "fg.muted" })}>
      {t("shell.live.connecting")}
    </output>
  );
}
